import { finalize } from 'rxjs/operators';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BannerNotificationsService, SanitizeService, SpinnerService } from '../../../../xform-compat';
import { Subscription } from 'rxjs';
import { DateTime } from 'luxon';
import { CONFIG } from '../../../../config';
import {
  AuthenticationService,
  AuthorizationService,
  CompanyEventsService, DateTimeService,
  FileUploadService,
  RmsUtilsService,
  TextContentService
} from '../../../../core/services';
import { Attachment, CompanySpace, Note, TextContent, User } from '../../../models';
import { TextContentHistoryModalComponent } from '../text-content-history-modal/text-content-history-modal.component';
import { CompanyNotesService } from '../../../../core/data-services/company';
import { PERMISSIONS } from '../../../enums/permissions.enum';
import { InsightsService } from '../../../../core/data-services/insights.service';
import { CrmNoteService } from '../../../../core/data-services/crm-note.service';
import { BaseCRMItem, CrmItemSnapshot } from '../../../models/crm/CRMItem.model';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CrmUtilsService } from 'src/app/core/data-services/crm-utils.service';
import { CrmItemType } from '../../../enums/crm-item-type.enum';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';


@Component({
    selector: 'rms-text-content-item',
    templateUrl: './text-content-item.component.html',
    styleUrls: ['./text-content-item.component.scss'],
    standalone: false
})
export class TextContentItemComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Matches the RMS system forwarding address, including any sub-address suffix
   * (e.g. note_to_RMS+qa@racap.com), so it can be hidden from the forward recipients.
   */
  private static readonly SYSTEM_ADDRESS_REGEX = /note_to_rms(\+[^@\s]*)?@racap\.com/i;

  @Input() allowEdition = true;
  @Input() isReferenceNote = false;
  @Input() attachmentsPreview: Array<Attachment>;
  @Input() copyPasteEnabled: boolean;
  @Input() currentSpace: CompanySpace;
  @Input() data: any;
  @Input() displayLastEdit: boolean;
  @Input() displayMaps: boolean;
  @Input() displayMnpi: boolean;
  @Input() displayOnlyAttachments = false;
  @Input() displayReferences: boolean;
  @Input() displaySelectCheckbox: boolean;
  @Input() headerActions: boolean;
  @Input() historyEndpoint: string;
  @Input() index: number;
  @Input() isNote: boolean;
  @Input() isReadOnly: boolean;
  @Input() length: number;
  @Input() overviewEnabled: boolean;
  @Input() crmItem: BaseCRMItem;
  @Input() crmNote: {id: number, textContent: TextContent, itemSnapshot: CrmItemSnapshot};
  @Input() crmItems: Array<BaseCRMItem> = [];
  @Output() reviewPosted = new EventEmitter<null>();
  @Output() startEdit: EventEmitter<any> = new EventEmitter<any>();
  @Output() onCrmChanges: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(TextContentHistoryModalComponent, { static: true }) historyModal: TextContentHistoryModalComponent;
  @ViewChild('convertOverviewNoteModal', { static: true }) convertOverviewNoteModal: ElementRef;
  @ViewChild('transferCrmModal', { static: true }) transferCrmModal: ElementRef;
  @ViewChild(ConfirmationModalComponent, { static: true }) discardDraftConfirmationModal: ConfirmationModalComponent;
  private autoSaveSubscription: Subscription;
  private filesToUpload: FileList = null;
  private fileUploadSubscription: Subscription;
  private selectedEditItem: any;
  /**
   * The note's Overview flag as the server last reported it during this edit - set when editing
   * begins and moved on each re-bind - so a re-bind can tell whether the reader has changed it
   * since (and their choice should win) or not (and the server's should).
   */
  private overviewAtEditStart: boolean;
  public allowedFileType = CONFIG.fileUploadAllowedExtensions;
  public autoSaveMessage = '';
  public currentUser: User;
  public editMode = false;
  public editorContent: any;
  public formatEST = CONFIG.formatEST;
  public PERMISSIONS = PERMISSIONS;
  public shortDateFormat = CONFIG.shortDateFormat;
  public timestampFormat = CONFIG.dateTimeFormatSeparate;
  public verboseDateTimeFormat = "MMMM d, yyyy 'at' h:mm a";
  public hasPermission: Record<string, boolean>;
  /** Whether the TARA "Original Email" metadata block is expanded (collapsed by default) */
  public emailMetadataExpanded = false;
  private ovnConverModalReference: NgbModalRef;
  private transferCrmModalReference: NgbModalRef;
  public transferCrm: BaseCRMItem;

  protected readonly CrmItemType = CrmItemType;

  /** Whether this note originated from a TARA email forward */
  get isTaraNote(): boolean {
    return !!this.data?.textContent?.isTaraNote || !!this.data?.textContent?.emailMetadata;
  }

  /** Forward "To" recipients excluding the RMS system address */
  get visibleForwardedToRecipients(): Array<string> {
    return (this.data?.textContent?.emailMetadata?.forwardedToRecipients ?? [])
      .filter((recipient: string) => !this.isSystemAddress(recipient));
  }

  /** Forward "CC" recipients excluding the RMS system address */
  get visibleForwardedCcRecipients(): Array<string> {
    return (this.data?.textContent?.emailMetadata?.forwardedCcRecipients ?? [])
      .filter((recipient: string) => !this.isSystemAddress(recipient));
  }

  /** Returns true when the recipient is the RMS system forwarding address */
  private isSystemAddress(recipient: string): boolean {
    return !!recipient && TextContentItemComponent.SYSTEM_ADDRESS_REGEX.test(recipient);
  }

  /** Toggles the TARA "Original Email" metadata block between collapsed and expanded */
  public toggleEmailMetadata(): void {
    this.emailMetadataExpanded = !this.emailMetadataExpanded;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private notesService: CompanyNotesService,
    private crmNoteService: CrmNoteService,
    private spinnerService: SpinnerService,
    private bannerNotificationService: BannerNotificationsService,
    private companyEventsService: CompanyEventsService,
    private insightsService: InsightsService,
    private utilsService: RmsUtilsService,
    public textContentService: TextContentService,
    public fileUploadService: FileUploadService,
    public authorizationService: AuthorizationService,
    private sanitizeService: SanitizeService,
    private modalService: NgbModal,
    public crmUtilsService: CrmUtilsService,
  ) {
    this.hasPermission = {
      crm: this.authorizationService.hasPermission([PERMISSIONS.SonarRead, PERMISSIONS.CoreSonarNoteRead]),
    };
    this.currentUser = this.authenticationService.getLoggedUser();
    this.overviewEnabled = false;
  }

  /**
   * Flags a draft whenever a new data object arrives, not only on first render: a list that
   * tracks its items by id re-binds a re-fetched note to the existing component instead of
   * creating a new one, and the re-fetched object carries no isDraft of its own. The flag is
   * derived from the status every time rather than only ever set, so it can never outlive the
   * draft on an object that is re-bound after being posted.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (!changes.data || !this.data) {
      return;
    }
    this.data.isDraft = this.data.textContent?.contentStatusType === 'DRAFT';
    if (this.data.isDraft) {
      this.data.isOpen = true;
    }
    this.rebindEditState(changes.data.previousValue);
  }

  /**
   * Keeps an open editor attached to the note when a re-fetch re-binds this component to a new
   * copy of it. The editor's text is kept, but saving submits selectedEditItem, so it is pointed
   * at the fresh copy rather than left on the superseded one. The Overview checkbox is bound to
   * data, so if the reader toggled it during this edit their choice is carried across; if not,
   * the fresh copy's value stands, so a change someone else made is not reverted. Either way the
   * baseline moves to the fresh copy's value, so the next re-bind in the same edit compares the
   * reader's checkbox against what the server last said rather than against where the edit began.
   * @param previous the data object this component was bound to before the change
   */
  private rebindEditState(previous: any): void {
    if (!this.editMode || !previous || this.selectedEditItem !== previous || previous.id !== this.data.id) {
      return;
    }
    const serverOverview = this.data.textContent?.isOverview;
    const toggledDuringEdit = previous.textContent?.isOverview !== this.overviewAtEditStart;
    if (toggledDuringEdit && this.data.textContent) {
      this.data.textContent.isOverview = previous.textContent.isOverview;
    }
    this.overviewAtEditStart = serverOverview;
    this.selectedEditItem = this.data;
  }

  ngOnInit() {
    this.fileUploadSubscription = this.fileUploadService.fileUploadResponse$.subscribe(value => {
      this.textContentService.updateAttachmentList(value);
    });
    this.autoSaveSubscription = this.companyEventsService.autoSaveDraftNotifier$.subscribe(value => {
      this.autoSaveMessage = value ?
        `Draft auto-saved at ${DateTimeService.convertToEst(value.textContent.timestamp, undefined, CONFIG.dateTimeFormatAlternative)}` :
        'Draft auto-saved locally';
    });
  }

  ngOnDestroy() {
    if (this.fileUploadSubscription) {
      this.fileUploadSubscription.unsubscribe();
    }
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
  }

  /**
   * Shows text content history modal
   * @param evt
   * @param data
   * @param isNote
   * @param isReferenceNote
   */
  public showHistoryModal(evt: MouseEvent, data: any, isNote: boolean, isReferenceNote?: boolean): void {
    evt.stopPropagation();
    if (isReferenceNote) {
      this.spinnerService.start();
      if (data.textContent.textContentType === 'INDICATION_NOTE') {
        this.notesService.getIndicationHistory(data.ownerId, data.id).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            value => this.historyModal.openModal(value),
            error1 => this.bannerNotificationService.error(error1)
          );
      } else if (data.textContent.textContentType === 'COMPANY_NOTE') {
        this.notesService.getNoteHistory(data.ownerId, data.id).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            value => this.historyModal.openModal(value),
            error1 => this.bannerNotificationService.error(error1)
          );
      }
    } else if (isNote) {
      this.spinnerService.start();
      if (this.historyEndpoint === 'indication') {
        this.notesService.getIndicationHistory(data.ownerId, data.id).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            value => this.historyModal.openModal(value),
            error1 => this.bannerNotificationService.error(error1)
          );
      } else if (this.historyEndpoint === 'insight') {
        this.notesService.getInsightHistory(data.id).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            value => this.historyModal.openModal(value),
            error1 => this.bannerNotificationService.error(error1)
          );
      } else if (this.crmNoteService.isCrmNote(data.textContent)) {
        this.crmNoteService.getNoteHistory(data.ownerId, data.id).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            value => this.historyModal.openModal(value),
            error1 => this.bannerNotificationService.error(error1)
          );
      } else {
        this.notesService.getNoteHistory(data.ownerId, data.id).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            value => this.historyModal.openModal(value),
            error1 => this.bannerNotificationService.error(error1)
          );
      }
    }
  }

  public showOverviewConvertModal(evt: MouseEvent): void {
    evt.stopPropagation();
    this.ovnConverModalReference = this.modalService.open(this.convertOverviewNoteModal,  { size: 'lg', centered: true });
    this.ovnConverModalReference.result.then(result => {
      if (result) {
        this.spinnerService.start();
        this.notesService.toggleNoteOverview(result).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            value => {
              this.bannerNotificationService.success('Note Updated');
              this.data.textContent.isOverview = value.isOverview;
            },
            error1 => this.bannerNotificationService.error(error1));
      }
    });
  }

  public showTransferCrmModal(evt: MouseEvent): void {
    evt.stopPropagation();
    this.transferCrmModalReference = this.modalService.open(this.transferCrmModal,  { size: 'md', centered: true });
    this.transferCrmModalReference.result.then(result => {
      if (result && result.noteId && result.crmId) {
        const { noteId, crmId } = result;
        this.transferCrm = null;
        this.spinnerService.start();
        this.notesService.transferCrmNoteToAnother(noteId, crmId).pipe(
          finalize(() => this.spinnerService.stop()))
          .subscribe(
            () => {
              this.bannerNotificationService.success('Note Updated');
              this.onCrmChanges.emit();
            },
            error1 => this.bannerNotificationService.error(error1));
      }
    });
  }
  /**
   * Starts edit item mode
   * @param evt
   * @param data
   */
  public startEditMode(evt?: MouseEvent, data?: any): void {
    if (evt) {
      evt.stopPropagation();
    }
    this.editMode = true;
    this.selectedEditItem = data;
    this.overviewAtEditStart = this.selectedEditItem?.textContent?.isOverview;
    this.editorContent = this.sanitizeService.sanitizeNoteContent(this.selectedEditItem.textContent.content);
    this.textContentService.editMode = true;
  }

  /**
   * Starts edit item mode
   * @param evt
   * @param data
   */
  public startReferenceAdditionMode(evt?: MouseEvent, data?: any): void {
    if (evt) {
      evt.stopPropagation();
    }
    this.textContentService.openReferenceModal({ currentNoteId: data.id,
      references: data.textContent.references,
      textContentId: data.textContent.id,
      isCrmNote: this.isCrmNote(data.textContent) });
  }

  /**
   * Triggers the selected event when the checkbox for the item es checked
   * @param evt
   * @param data
   */
  public setSelectedItems(evt: MouseEvent, data: any): void {
    if (evt) {
      evt.stopPropagation();
    }
    if (data.checked !== true) {
      this.insightsService.addInsightToEmailList(data);
    } else {
      this.insightsService.removeInsightFromEmailList(data);
    }
  }

  /**
   * Change detector in the rich text editor
   * @param evt
   */
  public editorChanged(evt: any) {
    this.editorContent = this.sanitizeService.sanitizeNoteContent(evt);
  }

  /**
   * Detects input file change and calls the method to upload
   * @param files
   */
  public inputFileChanged(files: FileList): void {
    this.filesToUpload = files;
    this.uploadFiles();
  }

  /**
   * If there is a valid until date value compares the current date to enable/disable the edit button
   * @param item
   */
  public isEditable(item: any): boolean {
    if (!item.textContent.validEditUntilDate) {
      return true;
    } else {
      const convertedTime = DateTimeService.parseDate(item.textContent.validEditUntilDate);
      const currentTime = DateTime.now();
      return currentTime < convertedTime;
    }
  }

  /**
   * Detects changes in the upload file form and triggers the submit
   */
  private uploadFiles(): void {
    this.fileUploadService.uploadFiles(this.filesToUpload, 'fileUploadEditNote', [], true);
  }

  /**
   * Notifies the text content service to save as draft a note
   * On click save as draft button
   * @param clickEvt
   */
  public saveAsDraft(clickEvt: MouseEvent) {
    if (!this.editorContent) {
      this.bannerNotificationService.warning('Text editor can\'t be empty');
      return;
    }
    this.selectedEditItem.textContent.isOverview = this.data.textContent.isOverview;
    this.textContentService.saveDraftNote(this.selectedEditItem,
      { content: this.sanitizeService.sanitizeNoteContent(this.editorContent), isOffline: false }, clickEvt);
    this.textContentService.checkAbsoluteImgSrc(this.editorContent);
    this.noteSubmitted();
  }

  /**
   * Notifies the text content service to update a note
   * @param editorContent
   */
  public postItem(editorContent: string) {
    if (!this.editorContent) {
      this.bannerNotificationService.warning('Text editor can\'t be empty');
      return;
    }
    this.selectedEditItem.textContent.isOverview = this.data.textContent.isOverview;
    this.textContentService.updateNote({
      currentNote: this.selectedEditItem,
      editorContent: this.sanitizeService.sanitizeNoteContent(editorContent)
    });
    this.textContentService.checkAbsoluteImgSrc(editorContent);
    this.noteSubmitted();
  }

  /**
   * Resets edit mode to read only content
   */
  private noteSubmitted(): void {
    this.editMode = false;
    this.editorContent = null;
    this.selectedEditItem = null;
    this.companyEventsService.textContentPosted();
  }

  /**
   * Detects editor auto save event and notifies the lister to save the note as draft
   * @param evt
   */
  public onAutoSave(evt: { content: string, isOffline: boolean }): void {
    this.textContentService.saveDraftNote(this.selectedEditItem, evt);
  }

  /**
   * Shows discard draft confirmation modal
   */
  public showDiscardDraftModal(): void {
    const confirmationModalRef = this.discardDraftConfirmationModal.openModal();
    confirmationModalRef.result.then((result) => {
      if (result) {
        this.discardDraft(this.data);
      }
    });
  }

  /**
   * Emits and event to notify the parent about discard a draft
   * @param selectedNote
   */
  public discardDraft(selectedNote: Note): void {
    this.textContentService.discardNoteDraft(selectedNote.id);
  }

  /**
   * Detect review posted and passes the form value to the parent
   * @param evt
   */
  public onReviewPosted(evt: any) {
    if (!this.isNote) {
      this.reviewPosted.emit(evt);
    }
  }

  /**
   * Copy the content of a current note and open a new editor
   * @param data
   */
  public copyNote(evt: MouseEvent, data: any): void {
    evt.stopPropagation();
    this.notesService.sendCopiedNote(data.textContent);
  }

  /**
   * redirects to the reference entity page
   * @param evt
   */
  public goToReferenceEntity(evt: MouseEvent): void {
    evt.stopPropagation();
    const path = this.data.textContent.textContentType === 'COMPANY_NOTE' ? '/company' :
      (this.data.textContent.textContentType === 'INDICATION_NOTE') ? '/indications' : '';
    window.open(`${window.location.origin}${path}/${this.data.textContentOwner.parentId}`);
  }

  /**
   * Copy link to clipboard
   * @param data
   */
   public copyLink (evt: MouseEvent, data: any): void {
    evt.stopPropagation();
    if (this.isReferenceNote) {
      if (data.textContent.textContentType === 'COMPANY_NOTE') {
        this.utilsService.copyToClipboard(
          `${location.origin}/company/${data.textContentOwner.parentId}?noteId=${data.textContent.id}`,
          'Link');
      } else if (data.textContent.textContentType === 'INDICATION_NOTE') {
        this.utilsService.copyToClipboard(
          `${location.origin}/indications/${data.textContentOwner.parentId}?noteId=${data.textContent.id}`,
          'Link');
      }
    } else {
      if (data.textContent.textContentType === 'COMPANY_NOTE' || this.crmNoteService.isCrmNote(data.textContent)) {
        this.utilsService.copyToClipboard(`${location.origin + location.pathname}?noteId=${data.textContent.id}`, 'Link');
      } else if (data.textContent.textContentType === 'INDICATION_NOTE') {
        this.utilsService.copyToClipboard(
          `${location.origin}/indications/${data.ownerId}?noteId=${data.textContent.id}`,
          'Link');
      }
    }
  }

  public isCrmNote(textContent: TextContent): boolean {
    return textContent.textContentType === 'CRM_NOTE';
  }
}
